import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  BookOpen, 
  Search, 
  Zap, 
  ShieldCheck, 
  Cpu, 
  Award, 
  ChevronRight, 
  Play, 
  FileText, 
  MousePointer2, 
  CheckCircle2, 
  Clock,
  ArrowRight,
  Info,
  Layers,
  Activity,
  Target,
  BarChart3
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn, logInteraction } from '../lib/utils';
import { ACADEMY_DATA, STRATEGY_TUTORIALS, LearningTrack, Course } from '../data/academyData';
import { GLOSSARY } from '../constants';

export default function AcademyModule() {
  const navigate = useNavigate();
  const [activeTrack, setActiveTrack] = useState<LearningTrack | null>(null);
  const [activeCourse, setActiveCourse] = useState<Course | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const filteredGlossary = GLOSSARY.filter(item => 
    item.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.definition.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="app-container space-y-8 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-[var(--app-text)] tracking-tight uppercase italic font-display">Adversyn Academy</h1>
          <p className="text-[var(--muted-text)] mt-1 font-medium">The integrated trading education system for institutional-grade mastery.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-text)]" size={16} />
            <input
              type="text"
              data-testid="academy-input-search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search academy..."
              className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl pl-10 pr-4 py-2 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600 transition-all w-64 shadow-sm placeholder:text-[var(--muted-text)] uppercase italic"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Main Content Area */}
        <div className="lg:col-span-8 space-y-8">
          
          {/* Learning Tracks */}
          {!activeTrack && !activeCourse && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-black text-[var(--app-text)] uppercase tracking-widest italic flex items-center gap-2">
                  <Award size={20} className="text-emerald-600" />
                  Learning Tracks
                </h2>
              </div>
              <div className="grid grid-cols-1 gap-6">
                {ACADEMY_DATA.map((track) => (
                  <motion.div
                    key={track.id}
                    data-testid="academy-card-track"
                    whileHover={{ x: 4 }}
                    onClick={() => {
                      logInteraction('Academy', 'Select Track', { track: track.title });
                      setActiveTrack(track);
                    }}
                    className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm hover:border-emerald-600/30 transition-all cursor-pointer group flex flex-col gap-6"
                  >
                    <div className="flex flex-col md:flex-row items-center gap-8">
                      {/* Left: Icon or Illustration */}
                      <div 
                        className="w-24 h-24 bg-emerald-600/10 rounded-2xl flex items-center justify-center text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white transition-all shrink-0 shadow-inner"
                      >
                        <track.icon size={48} />
                      </div>

                      {/* Center: Course Title and Description */}
                      <div 
                        className="flex-1 min-w-0 text-center md:text-left"
                      >
                        <h3 className="text-xl font-black text-[var(--app-text)] uppercase italic mb-2 tracking-tight group-hover:text-emerald-600 transition-colors">{track.title}</h3>
                        <p className="text-sm text-[var(--muted-text)] font-medium leading-relaxed max-w-2xl">{track.description}</p>
                      </div>

                      {/* Right: Progress Indicator and Action Button */}
                      <div className="w-full md:w-56 space-y-4">
                        <div className="flex items-end justify-between md:justify-end md:gap-4">
                          <div className="text-left md:text-right">
                            <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Track Progress</div>
                            <div className="text-2xl font-black text-emerald-600 italic leading-none">{track.progress}%</div>
                          </div>
                        </div>
                        <div className="w-full h-2 bg-[var(--input-bg)] rounded-full overflow-hidden shadow-inner">
                          <div className="h-full bg-emerald-600 transition-all duration-1000 shadow-[0_0_10px_rgba(16,185,129,0.5)]" style={{ width: `${track.progress}%` }} />
                        </div>
                        <button
                          data-testid="academy-btn-start"
                          onClick={(e) => {
                            e.stopPropagation();
                            logInteraction('Academy', 'Continue Learning', { track: track.title });
                            // Logic to open the first incomplete course/lesson
                            const firstCourse = track.courses[0];
                            setActiveTrack(track);
                            setActiveCourse(firstCourse);
                          }}
                          className="w-full py-2.5 bg-[var(--app-text)] text-[var(--card-bg)] rounded-xl text-xs font-black uppercase italic shadow-lg shadow-[var(--app-text)]/10 group-hover:bg-emerald-600 group-hover:text-white transition-all"
                        >
                          Continue Learning
                        </button>
                      </div>
                    </div>

                    {/* Footer Area */}
                    <div className="flex items-center pt-4 border-t border-[var(--card-header-border)]">
                      {/* Lower Left: Number of Courses */}
                      <div className="flex-1">
                        <span className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest flex items-center gap-2">
                          <Layers size={14} className="text-emerald-600" />
                          {track.courses.length} Specialized Courses
                        </span>
                      </div>

                      {/* Lower Middle: View Track */}
                      <div className="flex-1 text-center">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            logInteraction('Academy', 'View Track', { track: track.title });
                            setActiveTrack(track);
                          }}
                          className="inline-flex items-center gap-1 text-emerald-600 font-black text-[10px] uppercase italic tracking-widest hover:gap-2 transition-all"
                        >
                          View Learning Track <ChevronRight size={14} />
                        </button>
                      </div>

                      {/* Spacer for balance */}
                      <div className="flex-1 hidden md:block" />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Active Track View */}
          {activeTrack && !activeCourse && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <button
                data-testid="academy-btn-back"
                onClick={() => {
                  logInteraction('Academy', 'Back to Tracks');
                  setActiveTrack(null);
                }}
                className="flex items-center gap-2 text-xs font-black text-[var(--muted-text)] hover:text-emerald-600 transition-colors uppercase italic"
              >
                <ArrowRight size={14} className="rotate-180" /> Back to Tracks
              </button>
              
              <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-8 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-emerald-600/20">
                      <activeTrack.icon size={32} />
                    </div>
                    <div>
                      <h2 className="text-2xl font-black text-[var(--app-text)] uppercase italic">{activeTrack.title}</h2>
                      <p className="text-[var(--muted-text)] font-medium">{activeTrack.description}</p>
                    </div>
                  </div>
                  <button
                    data-testid="academy-btn-start"
                    onClick={() => {
                      logInteraction('Academy', 'Start Learning', { track: activeTrack.title });
                      setActiveCourse(activeTrack.courses[0]);
                    }}
                    className="px-6 py-3 bg-emerald-600 text-white rounded-xl text-sm font-black uppercase italic shadow-lg shadow-emerald-600/20 hover:bg-emerald-500 transition-all flex items-center gap-2 shrink-0"
                  >
                    <Play size={18} className="fill-white" />
                    Start Learning
                  </button>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xs font-black text-[var(--muted-text)] uppercase tracking-widest italic mb-2">Track Courses</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {activeTrack.courses.map((course) => (
                      <div
                        key={course.id}
                        data-testid="academy-card-course"
                        className="flex items-center justify-between p-4 bg-[var(--input-bg)] border border-[var(--card-border)] rounded-xl hover:border-emerald-600/30 transition-all cursor-pointer group"
                        onClick={() => {
                          logInteraction('Academy', 'Select Course', { course: course.title });
                          setActiveCourse(course);
                        }}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-[var(--card-bg)] flex items-center justify-center text-[var(--muted-text)] group-hover:text-emerald-600 transition-colors">
                            <course.icon size={20} />
                          </div>
                          <div>
                            <h4 className="text-sm font-black text-[var(--app-text)] uppercase italic">{course.title}</h4>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-[9px] font-black px-1.5 py-0.5 bg-emerald-600/10 text-emerald-600 rounded uppercase tracking-widest">{course.level}</span>
                              <span className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">{course.lessons.length} Lessons</span>
                            </div>
                          </div>
                        </div>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            logInteraction('Academy', 'Start Course', { course: course.title });
                            setActiveCourse(course);
                          }}
                          className="flex items-center gap-2 text-emerald-600 font-black text-xs uppercase italic transition-all hover:gap-3"
                        >
                          Start Course <Play size={14} className="fill-emerald-600" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Active Course View */}
          {activeCourse && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <button 
                onClick={() => {
                  logInteraction('Academy', 'Back to Track');
                  setActiveCourse(null);
                }}
                className="flex items-center gap-2 text-xs font-black text-[var(--muted-text)] hover:text-emerald-600 transition-colors uppercase italic"
              >
                <ArrowRight size={14} className="rotate-180" /> Back to Track
              </button>

              <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-sm">
                <div className="p-8 border-b border-[var(--card-header-border)] bg-[var(--card-header-bg)]">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center text-white">
                      <activeCourse.icon size={24} />
                    </div>
                    <div>
                      <h2 className="text-xl font-black text-[var(--app-text)] uppercase italic">{activeCourse.title}</h2>
                      <p className="text-sm text-[var(--muted-text)] font-medium">{activeCourse.description}</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-8 space-y-4">
                  {activeCourse.lessons.map((lesson, idx) => (
                    <div key={lesson.id} className="flex items-center justify-between p-4 rounded-xl hover:bg-[var(--input-bg)] transition-all group border border-transparent hover:border-[var(--input-border)]">
                      <div className="flex items-center gap-4">
                        <div className="text-xs font-black text-[var(--muted-text)] w-6">{idx + 1}</div>
                        <div className={cn(
                          "w-8 h-8 rounded-lg flex items-center justify-center",
                          lesson.completed ? "bg-emerald-600/10 text-emerald-600" : "bg-[var(--input-bg)] text-[var(--muted-text)]"
                        )}>
                          {lesson.type === 'video' && <Play size={14} className={lesson.completed ? "fill-emerald-600" : ""} />}
                          {lesson.type === 'reading' && <FileText size={14} />}
                          {lesson.type === 'interactive' && <MousePointer2 size={14} />}
                          {lesson.type === 'quiz' && <Award size={14} />}
                        </div>
                        <div>
                          <h5 className="text-sm font-black text-[var(--app-text)] uppercase italic">{lesson.title}</h5>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest flex items-center gap-1">
                              <Clock size={10} /> {lesson.duration}
                            </span>
                            <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest">{lesson.type}</span>
                          </div>
                        </div>
                      </div>
                      {lesson.completed ? (
                        <CheckCircle2 size={18} className="text-emerald-600" />
                      ) : (
                        <Link to={`/academy/module/${lesson.id}`}>
                          <button 
                            className="px-4 py-1.5 bg-[var(--app-text)] text-[var(--card-bg)] rounded-lg text-[10px] font-black uppercase italic transition-all hover:bg-emerald-600 hover:text-white cursor-pointer active:scale-95"
                          >
                            Start
                          </button>
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Strategy Tutorials */}
          {!activeTrack && !activeCourse && (
            <div className="space-y-6">
              <h2 className="text-xl font-black text-[var(--app-text)] uppercase tracking-widest italic flex items-center gap-2">
                <Zap size={20} className="text-emerald-600" />
                Strategy Tutorials
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {STRATEGY_TUTORIALS.map((strat) => (
                  <div 
                    key={strat.id} 
                    onClick={() => {
                      logInteraction('Academy', 'Select Strategy Tutorial', { tutorial: strat.title });
                      // For now, just show a notification or navigate to a generic lesson
                      // In a real app, this would link to a specific tutorial module
                      const firstTrack = ACADEMY_DATA[0];
                      const firstCourse = firstTrack.courses[0];
                      setActiveTrack(firstTrack);
                      setActiveCourse(firstCourse);
                    }}
                    className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl p-4 shadow-sm hover:border-emerald-600/30 transition-all cursor-pointer group"
                  >
                    <div className="w-10 h-10 bg-[var(--input-bg)] rounded-lg flex items-center justify-center text-[var(--muted-text)] group-hover:bg-emerald-600 group-hover:text-white transition-all mb-3">
                      <strat.icon size={20} />
                    </div>
                    <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic mb-1">{strat.title}</h4>
                    <p className="text-[10px] text-[var(--muted-text)] font-medium leading-relaxed">{strat.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar Area: Glossary & Certifications */}
        <div className="lg:col-span-4 space-y-8">
          
          {/* Certifications */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <h3 className="text-sm font-black text-[var(--app-text)] uppercase tracking-widest italic mb-6 flex items-center gap-2">
              <Award size={18} className="text-emerald-600" />
              Your Certifications
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-3 bg-emerald-600/5 border border-emerald-600/20 rounded-xl">
                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center text-white shadow-lg shadow-emerald-600/20">
                  <CheckCircle2 size={24} />
                </div>
                <div>
                  <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic">Market Foundations</h4>
                  <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-widest">Completed May 12, 2026</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-3 bg-[var(--input-bg)] border border-dashed border-[var(--input-border)] rounded-xl opacity-50">
                <div className="w-12 h-12 bg-[var(--input-border)] rounded-full flex items-center justify-center text-[var(--muted-text)]">
                  <Award size={24} />
                </div>
                <div>
                  <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic">Options Specialist</h4>
                  <p className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Locked</p>
                </div>
              </div>
            </div>
          </div>

          {/* Glossary / Legend */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm flex flex-col h-[500px]">
            <h3 className="text-sm font-black text-[var(--app-text)] uppercase tracking-widest italic mb-2 flex items-center gap-2">
              <BookOpen size={18} className="text-emerald-600" />
              Trading Glossary
            </h3>
            <a
              href="/glossary"
              data-testid="academy-link-full-glossary"
              className="mb-4 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-600 hover:text-emerald-500"
            >
              Open full Glossary →
            </a>
            <div className="flex-1 overflow-y-auto space-y-4 pr-2 scrollbar-hide">
              {filteredGlossary.map((item, i) => (
                <div key={i} className="space-y-1 group">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic group-hover:text-emerald-600 transition-colors">{item.term}</h4>
                    <span className="text-[8px] font-black px-1.5 py-0.5 bg-[var(--input-bg)] text-[var(--muted-text)] rounded uppercase tracking-widest">{item.category}</span>
                  </div>
                  <p className="text-[10px] text-[var(--muted-text)] leading-relaxed font-medium">{item.definition}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
